import React, {
  type MouseEventHandler,
  type TouchEventHandler,
  useContext,
  useEffect,
  useState,
} from 'react';
import { hapticFeedbackStyle } from '@components/helpers/hapticFeedbackFunction';
import { type ImageFormats } from '@components/helpers/imagesImports';
import { useHighlightComponentRect } from '@components/Intro/hooks/highlight-component-rect.ts';
import { TapEffect } from '@components/ui/TapEffect/TapEffect';
import { tapResource } from '@redux/reducers/quarryReducer';
import { changeEnergy } from '@redux/reducers/userReducer';
import { selectQuarryById } from '@redux/selectors/quarrySelectors';
import { useAppDispatch, useAppSelector } from '@redux/store';
import cn from 'classnames';
import { cubicBezier, useAnimate } from 'framer-motion';
import mergeRefs from 'merge-refs';
import { v4 as uuidv4 } from 'uuid';

import { useIntroState } from '@/common/hooks/intro-ref';
import { type ResourceTypeText } from '@/common/types/resource';
import { Picture } from '@/components/ui/Picture/Picture';
import { ClaimAnimationContext } from '@/context/ClaimAnimationContext';
import { MineResourceContext } from '@/context/MineResourceContext';

interface TapEffectProps {
  id: number | string;
  x: number;
  y: number;
}

type props = {
  name: ResourceTypeText;
  id: string;
  img: ImageFormats;
  tap: number;
  claimModal: (tekst: string) => void;
  disabled?: boolean;
};

const ImgTapping: React.FC<props> = ({
  id,
  img,
  tap,
  claimModal,
  disabled,
}) => {
  const { isAnimationStart } = useContext(ClaimAnimationContext);
  const { showAnimation } = useIntroState(5);
  const ref = useHighlightComponentRect(5);
  const { setTappingIsStarted } = useContext(MineResourceContext);
  const [scope, animate] = useAnimate();
  const userEnergy = useAppSelector((state) => state.user.extendedInfo?.energy);
  const [tapEffects, setTapEffects] = useState<TapEffectProps[]>([]);
  const quarry = useAppSelector((state) => selectQuarryById(state, id));
  const dispatch = useAppDispatch();
  const { refTapElem } = useContext(ClaimAnimationContext);

  // useEffect(() => {
  //   console.log(tapEffects);
  // }, [tapEffects]);

  useEffect(() => {
    const animation = async (): Promise<void> => {
      if (isAnimationStart) {
        if (!scope.current) return;
        await animate(
          scope.current,
          { scale: 0.3, opacity: 0 },
          {
            ease: cubicBezier(0.36, 0, 0.66, -0.56),
            duration: 0.4,
          },
        );

        if (!scope.current) return;
        await animate(
          scope.current,
          { scale: 1, opacity: 1 },
          {
            delay: 0.7,
            ease: 'easeIn',
            duration: 0.4,
          },
        );
      }
    };

    animation();
  }, [isAnimationStart]);

  const onAnimationEnd = (id: string | number): void => {
    setTapEffects((state) => state.filter((effect) => effect.id !== id));
  };

  const onTap = ({
    isTrusted,
    clientX,
    clientY,
  }: {
    isTrusted: boolean;
    clientX: number;
    clientY: number;
  }): void => {
    const energy = userEnergy || 0;

    if (!isTrusted || !quarry || energy < 1) {
      return;
    }

    if (quarry.localStock >= quarry.storageCapacity) {
      claimModal('Хранилище заполнено');

      return;
    }

    dispatch(tapResource({ id: quarry.id, quantity: 1 }));
    dispatch(changeEnergy(-1));

    setTappingIsStarted((state) => (!state ? true : state));
    animate(
      scope.current,
      { scale: [1, 0.85, 1] },
      { ease: 'easeOut', duration: 0.3 },
    );

    const newEffect = {
      id: uuidv4(),
      x: clientX,
      y: clientY,
    };
    setTapEffects((prevEffects) => [...prevEffects, newEffect]);
  };

  const handleMouseDown: MouseEventHandler<HTMLDivElement> = (event) => {
    if (disabled) {
      return;
    }

    if (!event.target) {
      return;
    }

    const { isTrusted, clientX, clientY } = event;
    onTap({ isTrusted, clientX, clientY });
  };

  const onTouchStart: TouchEventHandler<HTMLDivElement> = (event) => {
    if (disabled) {
      return;
    }
    const availableEnergy = userEnergy || 0;
    const touches = Array.from(event.changedTouches).slice(0, 6);
    const tapsToProcess = Math.min(availableEnergy, touches.length);
    for (let i = 0; i < tapsToProcess; i++) {
      const { clientX, clientY } = touches[i];
      onTap({ isTrusted: event.isTrusted, clientX, clientY });
    }
    hapticFeedbackStyle('light');
  };

  return (
    <>
      <div
        id="intro-step-5"
        onTouchStart={navigator.maxTouchPoints ? onTouchStart : undefined}
        onClick={!navigator.maxTouchPoints ? handleMouseDown : undefined}
        ref={mergeRefs(scope, refTapElem, ref)}
        className={cn(
          'mine-hero__img rounded-full cursor-pointer min-h-[0%]',
          showAnimation && 'relative pointer-events-auto z-[11]',
        )}
      >
        <Picture img={img.png} webp={img.webp} alt={'mine'} />
      </div>
      {tapEffects.map((effect) => (
        <TapEffect
          key={effect.id}
          onAnimationEnd={onAnimationEnd}
          value={tap}
          id={effect.id}
          top={effect.y}
          left={effect.x}
        />
      ))}
    </>
  );
};

export default ImgTapping;
